/**
 * @file        src/pages/platform/ReadinessChecklist.jsx
 * @description Renders GET /api/tenants/:id/readiness — the go-live checklist
 *              (URLs, DB connection, migrations, brands, gateways, branding,
 *              supplier credentials). Each failing check deep-links to the
 *              Tenant Management page that fixes it (?tenant=<id> preselects
 *              the tenant). Used by the tenant edit page (as the onboarding
 *              progress panel) and by the Tenant List's Go Live dialog, which
 *              also gates its Activate button on `ready` — though the real
 *              boundary is the server re-running the same checks on
 *              PATCH status→active.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (tenant go-live workflow).
 *   - 2026-08-10 Claude — 'company' fix-link (the issuing company printed on
 *                          invoices/tickets) → Tenant Management → Issuing
 *                          Company.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';

// Which Tenant Management page fixes which failing check.
const FIX_LINK = {
  urls: (id) => `/platform/tenants/${id}/edit`,
  brands: (id) => `/platform/tenants/brands?tenant=${id}`,
  gateways: (id) => `/platform/tenants/gateways?tenant=${id}`,
  branding: (id) => `/platform/tenants/branding?tenant=${id}`,
  supplier: (id) => `/platform/tenants/suppliers?tenant=${id}`,
  database: (id) => `/platform/tenants/${id}/edit`,
  // The tenant admin owns this too (Site Setting), but a PENDING tenant's own
  // host 404s, so during onboarding the platform page is the only way in.
  company: (id) => `/platform/tenants/company?tenant=${id}`,
};

export function useReadiness(tenantId) {
  return useQuery({
    queryKey: qk.tenants.readiness(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/readiness`).then((r) => r.data),
    enabled: Boolean(tenantId),
    // Each check hits the tenant's DB — don't refire on every focus.
    staleTime: 30 * 1000,
  });
}

export default function ReadinessChecklist({ tenantId, readiness, isLoading, isError }) {
  if (isLoading) return <div className="empty">Running readiness checks…</div>;
  if (isError || !readiness) return <div className="empty"><p className="field-error">Could not run the readiness checks.</p></div>;

  return (
    <ul className="readiness-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
      {readiness.checks.map((c) => (
        <li key={c.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span aria-hidden style={{ color: c.ok ? 'var(--ok, #16a34a)' : 'var(--err, #dc2626)', lineHeight: 1.4 }}>
            <Icon name={c.ok ? 'check' : 'x'} />
          </span>
          <div>
            <b>{c.label}</b>
            {c.detail ? <div className={c.ok ? 'muted' : 'field-error'} style={{ fontSize: 13 }}>{c.detail}</div> : null}
            {!c.ok && FIX_LINK[c.key] ? (
              <Link to={FIX_LINK[c.key](tenantId)} className="muted" style={{ fontSize: 13, textDecoration: 'underline' }}>
                Fix this →
              </Link>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
